import type { PlanDefinition } from "@medplum/fhirtypes";
import { type AnsweredValue, type ParameterAnswer, ProtocolDefinition } from "@repo/domain";
import { readProtocolDefinition } from "../fhir/protocol-definition-storage";
import type { ProtocolAnswerValueDto, ProtocolDefinitionDto, ProtocolParameterAnswerDto } from "./protocol-definition";

export interface ProtocolDefinitionProjectionFailure {
  resourceId: string | undefined;
  name: string;
  message: string;
}

/** Keep a malformed catalog record from hiding valid protocols; callers surface failures. */
export function projectProtocolDefinitionRows(resources: readonly PlanDefinition[]): {
  definitions: ProtocolDefinitionDto[];
  failures: ProtocolDefinitionProjectionFailure[];
} {
  const definitions: ProtocolDefinitionDto[] = [];
  const failures: ProtocolDefinitionProjectionFailure[] = [];
  for (const resource of resources) {
    try {
      definitions.push(projectProtocolDefinitionRow(resource));
    } catch (error) {
      failures.push({
        resourceId: resource.id,
        name: resource.title ?? resource.url ?? "Unnamed protocol",
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return { definitions, failures };
}

export function projectProtocolDefinitionRow(resource: PlanDefinition): ProtocolDefinitionDto {
  // The row is read by `fhir/protocol-definition-storage.ts` and validated by
  // the entity, which is also the one place the visit's extent is computed —
  // a second sum here would be a second answer to `durationMinutes`.
  const { entity, header } = readProtocolEntity(resource);
  const definition = entity.facts;
  const durationMinutes = entity.durationMinutes;
  const steps = definition.steps.map((step) => ({
    placement: { ...step.placement },
    ...(step.title === undefined ? {} : { title: step.title }),
    occurrences: step.occurrences.map((occurrence) => ({
      occurrenceId: String(occurrence.id),
      justifications: occurrence.justifications?.map((reason) =>
        reason.kind === "exam" ? { kind: "exam" as const, exam: String(reason.exam) } : { kind: "authored" as const },
      ),
      definitionUrl: String(occurrence.definition),
      ...(occurrence.qualifier === undefined ? {} : { qualifier: occurrence.qualifier }),
      parameterValues: (occurrence.parameterValues ?? []).map(projectParameterAnswer),
      ...(occurrence.observationLevels?.length ? { observationLevels: occurrence.observationLevels } : {}),
      ...(occurrence.plannedTrials?.length
        ? {
            plannedTrials: occurrence.plannedTrials.map((trial) => ({
              id: String(trial.id),
              values: trial.values.map(projectParameterAnswer),
            })),
          }
        : {}),
    })),
    durationMinutes: step.placement.durationMinutes,
  }));
  return {
    ...header,
    envelopeMinutes: durationMinutes,
    steps,
    examDefinitionUrls: entity.examRefs.map(String),
    totalDurationMinutes: durationMinutes,
  };
}

type ProtocolHeader = Pick<
  ProtocolDefinitionDto,
  "definitionUrl" | "version" | "mintedAt" | "name" | "description" | "availability"
>;

function readProtocolEntity(resource: PlanDefinition) {
  const entity = new ProtocolDefinition(readProtocolDefinition(resource));
  const { spine } = entity.facts;
  const header: ProtocolHeader = {
    definitionUrl: spine.url,
    version: spine.version,
    mintedAt: spine.mintedAt,
    name: spine.name,
    ...(spine.description === undefined ? {} : { description: spine.description }),
    availability: spine.availability,
  };
  return { entity, header };
}

function projectParameterAnswer(answer: ParameterAnswer): ProtocolParameterAnswerDto {
  return { source: String(answer.source), parameterId: String(answer.parameterId), value: projectAnswer(answer.value) };
}

function projectAnswer(value: AnsweredValue): ProtocolAnswerValueDto {
  if (value.kind !== "Choice") return { ...value };
  return { ...value, selected: value.selected.map(String) };
}
